import React from 'react';

import {SerializedError} from '@reduxjs/toolkit';
import {FetchBaseQueryError} from '@reduxjs/toolkit/query';

import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {
  LoadingBreadcrumbName,
  LoadingPageContent,
  LoadingPageTitle,
} from 'app/general/loading/DetailLoadingPage';
import {SPPage} from 'app/general/structures/SPPage';
import {gt} from 'app/utils/i18n';

import {
  AccountOverviewSidebar,
  Props as AccountOverviewSidebarProps,
} from './AccountOverviewSidebar';

interface Props<T> {
  title?: string;
  titleNode?: React.ReactNode;
  location: AccountOverviewSidebarProps['location'];
  breadcrumb?: string;
  additionalBreadcrumbProps?: {[prop: string]: React.ReactNode};
  content: T | undefined;
  error: SerializedError | FetchBaseQueryError | undefined;
  isLoading: boolean;

  children: (content: T) => React.ReactNode;

  noResultsContent?: JSX.Element | string;
  notFoundContent?: {head: JSX.Element | string; body: React.ReactNode};
  errorContent?: {head?: JSX.Element | string; body?: React.ReactNode};
}

export function AccountPageShell<T>({
  title,
  titleNode,
  location,
  breadcrumb,
  additionalBreadcrumbProps = {},
  content,
  error,
  isLoading,
  children,

  noResultsContent,
  notFoundContent,
  errorContent,
}: Props<T>): JSX.Element {
  const is404Error = error != null && 'status' in error && error.status === 404;

  const alternativeContent =
    isLoading || content != null ? null : is404Error ? (
      <NoResultsMessage
        icon="fa-exclamation-triangle text-warning"
        head={notFoundContent?.head ?? gt('Not found')}
      >
        {notFoundContent?.body}
      </NoResultsMessage>
    ) : error != null ? (
      <NoResultsMessage
        icon="fa-exclamation-triangle text-warning"
        head={errorContent?.head ?? gt('Something went wrong')}
      >
        {errorContent?.body ?? (
          <p className="lead mb-0">
            <p>{gt('Please try again later')}.</p>
          </p>
        )}
      </NoResultsMessage>
    ) : noResultsContent ? (
      <NoResultsMessage head={noResultsContent} onSidebar />
    ) : null;

  return (
    <SPPage
      pageTitle={title}
      horizontalPadding={false}
      breadcrumbProps={{
        docName: breadcrumb ?? title ?? <LoadingBreadcrumbName />,
        ...additionalBreadcrumbProps,
      }}
    >
      <div className="container-fluid">
        <div className="row">
          <AccountOverviewSidebar location={location} />
          <main className="mt-5 mb-4 col-md-9 col-xl-10">
            {titleNode ??
              (title != null ? (
                <h1 className="border-bottom pb-2 mb-4">{title}</h1>
              ) : (
                <LoadingPageTitle className="border-bottom pb-2 mb-4" />
              ))}
            {alternativeContent ??
              (content == null ? <LoadingPageContent /> : children(content))}
          </main>
        </div>
      </div>
    </SPPage>
  );
}
